import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

interface AdminUser {
  email: string;
  role: string;
}

interface AdminAuthContextType {
  admin: AdminUser | null;
  loading: boolean;
  requestOtp: (email: string) => Promise<{ devOtp: string | null }>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem("kiba_admin_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/admin/me");
      setAdmin(data);
    } catch {
      localStorage.removeItem("kiba_admin_token");
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const requestOtp = async (email: string) => {
    const { data } = await api.post("/admin/request-otp", { email });
    return { devOtp: data.dev_otp ?? null };
  };

  const verifyOtp = async (email: string, otp: string) => {
    const { data } = await api.post("/admin/verify-otp", { email, otp });
    localStorage.setItem("kiba_admin_token", data.token);
    // role is returned directly in the verify-otp response — no second round-trip needed
    setAdmin({ email: data.email, role: data.role });
  };

  const logout = async () => {
    try {
      await api.post("/admin/logout");
    } catch {
      /* noop */
    }
    localStorage.removeItem("kiba_admin_token");
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loading, requestOtp, verifyOtp, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
